// Localization helpers that emit *Latin-digit* output (e.g. "2026/04/28 03:05 PM").
// For Arabic-Indic digits (e.g. "٢٠٢٦/٠٤/٢٨ ٠٣:٠٥ مساءً") used in the reception desk
// flows, see `receptionArabicDateTime.js`.

import store from '@/store';

export function extractDateParts(datetime) {
  if (!datetime) {
    return null;
  }

  const stringValue = String(datetime).trim();
  const matchedDate = stringValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (matchedDate) {
    return {
      year: matchedDate[1],
      month: matchedDate[2],
      day: matchedDate[3],
    };
  }

  const date = new Date(datetime);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return {
    year: String(date.getFullYear()),
    month: String(date.getMonth() + 1).padStart(2, '0'),
    day: String(date.getDate()).padStart(2, '0'),
  };
}

export function formatLocalizedDate(datetime, { locale, formatDate }) {
  if (locale !== 'ar') {
    return formatDate(datetime);
  }

  const dateParts = extractDateParts(datetime);
  if (!dateParts) {
    return formatDate(datetime);
  }

  return `${dateParts.year}/${dateParts.month}/${dateParts.day}`;
}

const DEFAULT_DISPLAY_TIME_ZONE = 'Asia/Hebron';

export function getTenantDisplayTimeZone() {
  const tenantSetting = store.state.settings.tenantSetting;
  const timeZone = tenantSetting && tenantSetting.timezone;
  const normalizedTimeZone = timeZone ? String(timeZone).trim() : '';
  if (!normalizedTimeZone) {
    return DEFAULT_DISPLAY_TIME_ZONE;
  }

  try {
    new Intl.DateTimeFormat('en-US', { timeZone: normalizedTimeZone }).format(new Date());
    return normalizedTimeZone;
  } catch {
    return DEFAULT_DISPLAY_TIME_ZONE;
  }
}

export function formatLocalizedDateTime(datetime, {
  locale, formatDate, t,
}) {
  if (locale !== 'ar') {
    return formatDate(datetime);
  }

  if (datetime == null || datetime === '') {
    return formatDate(datetime);
  }

  const date = new Date(datetime);
  if (Number.isNaN(date.getTime())) {
    return formatDate(datetime);
  }

  const dateParts = {
    year: String(date.getFullYear()),
    month: String(date.getMonth() + 1).padStart(2, '0'),
    day: String(date.getDate()).padStart(2, '0'),
  };
  const hours24 = date.getHours();
  const meridiem = hours24 < 12
    ? t('generalMessages.meridiem.am')
    : t('generalMessages.meridiem.pm');
  const hours12 = String(hours24 % 12 || 12).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${dateParts.year}/${dateParts.month}/${dateParts.day} ${hours12}:${minutes} ${meridiem}`;
}

// The time half of formatLocalizedDateTime, for surfaces that show a time on its own (a calendar
// event, a slot range). Non-Arabic locales get the tenant's own timeFormat.
export function formatLocalizedTime(datetime, { locale, formatDate, t }) {
  if (locale !== 'ar') {
    return formatDate(datetime, false, true);
  }

  if (datetime == null || datetime === '') {
    return formatDate(datetime, false, true);
  }

  const date = new Date(datetime);
  if (Number.isNaN(date.getTime())) {
    return formatDate(datetime, false, true);
  }

  const hours24 = date.getHours();
  const meridiem = hours24 < 12
    ? t('generalMessages.meridiem.am')
    : t('generalMessages.meridiem.pm');

  return `${String(hours24 % 12 || 12).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')} ${meridiem}`;
}

// Looks up the Arabic payments.paymentMethods.* key for a cash/credit-card/cheque
// label or code. Returns '' if there's no canonical match — callers decide on a fallback.
export function getLocalizedPaymentMethodLabel(label, { locale, t } = {}) {
  if (locale !== 'ar' || !t || !label) {
    return '';
  }

  const normalized = String(label).trim().toLowerCase();
  if (normalized === 'cash') return t('payments.paymentMethods.cash');
  if (normalized === 'credit card' || normalized === 'credit-card') {
    return t('payments.paymentMethods.creditCard');
  }
  if (normalized === 'cheque') return t('payments.paymentMethods.cheque');
  return '';
}

export function getLocalizedPaymentMethodName(paymentMethod, { locale, t }) {
  if (!paymentMethod) {
    return '';
  }

  if (locale === 'ar') {
    const canonicalLabel = getLocalizedPaymentMethodLabel(paymentMethod.code, { locale, t })
      || getLocalizedPaymentMethodLabel(paymentMethod.nameEn || paymentMethod.name, { locale, t });
    if (canonicalLabel) {
      return canonicalLabel;
    }

    return paymentMethod.nameAr || paymentMethod.name || paymentMethod.nameEn || '';
  }

  return paymentMethod.nameEn || paymentMethod.name || '';
}

export const PAYMENT_STATUS_TRANSLATION_KEYS = {
  confirmed: 'payments.statuses.confirmed',
  paid: 'payments.statuses.paid',
  pending: 'payments.statuses.pending',
  failed: 'payments.statuses.failed',
  rejected: 'payments.statuses.rejected',
  cancelled: 'payments.statuses.cancelled',
  refunded: 'payments.statuses.refunded',
};

// Lowercase keys (matched against `getLocalizedStatusName` which lowercases code/nameEn).
// Distinct from `REPORT_MEMBERSHIP_STATUS_TRANSLATION_KEYS` in reportLocalizationMaps,
// which is keyed by the raw (PascalCase) report value.
export const MEMBERSHIP_STATUS_TRANSLATION_KEYS = {
  active: 'membership.statuses.active',
  frozen: 'membership.statuses.frozen',
  expired: 'membership.statuses.expired',
  inactive: 'membership.statuses.inactive',
  cancelled: 'membership.statuses.cancelled',
};

export function getLocalizedStatusName(status, {
  locale,
  t,
  translationKeys = PAYMENT_STATUS_TRANSLATION_KEYS,
} = {}) {
  if (!status) {
    return '';
  }

  if (locale === 'ar') {
    if (status.nameAr) {
      return status.nameAr;
    }

    if (t && translationKeys) {
      const codeKey = String(status.code || '').toLowerCase();
      const nameKey = String(status.nameEn || status.name || '').toLowerCase();
      const translationKey = translationKeys[codeKey] || translationKeys[nameKey];
      if (translationKey) {
        return t(translationKey);
      }
    }

    return status.nameEn || status.name || '';
  }

  return status.nameEn || status.name || status.nameAr || '';
}

export function getLocalizedDisplayName(item, { locale } = {}) {
  if (!item) {
    return '';
  }

  if (locale === 'ar') {
    return item.nameAr || item.name || item.nameEn || '';
  }

  return item.nameEn || item.name || item.nameAr || '';
}
